import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import EcommerceNav from '../../components/ecommerce/EcommerceNav';
import EcommerceFooter from '../../components/ecommerce/EcommerceFooter';
import './Ecommerce.css';
import './ProductDetail.css';
import { formatINR } from '../../utils/currency';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addingToWishlist, setAddingToWishlist] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [buyingNow, setBuyingNow] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/ecommerce/products/${slug}`);
      
      if (response.data.success) {
        setProduct(response.data.data.product);
        setReviews(response.data.data.reviews);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      navigate('/ecommerce');
    } finally {
      setLoading(false);
    }
  };

  const checkWishlistStatus = async () => {
    if (!user) return;
    
    try {
      const response = await api.get('/api/ecommerce/wishlist');
      if (response.data.success) {
        const isInWishlist = response.data.data.some(
          item => item.product._id === product._id
        );
        setInWishlist(isInWishlist);
      }
    } catch (error) {
      console.error('Error checking wishlist:', error);
    }
  };

  useEffect(() => {
    if (product && user) {
      checkWishlistStatus();
    }
  }, [product, user]);

  const handleAddToCart = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setAddingToCart(true);
        await api.post('/api/ecommerce/cart/add', {
        productId: product._id,
        quantity,
        variant: selectedVariant
      });
      
      // Show success message (you can implement a toast notification)
      alert('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding product to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleAddToWishlist = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setAddingToWishlist(true);
      if (inWishlist) {
        await api.delete(`/api/ecommerce/wishlist/remove/${product._id}`);
        setInWishlist(false);
        alert('Removed from wishlist');
      } else {
        await api.post('/api/ecommerce/wishlist/add', {
          productId: product._id
        });
        setInWishlist(true);
        alert('Added to wishlist');
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      alert('Error updating wishlist');
    } finally {
      setAddingToWishlist(false);
    }
  };

  const handleBuyNow = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!isInStock()) {
      alert('Product is out of stock');
      return;
    }

    try {
      setBuyingNow(true);
      const response = await api.post('/api/ecommerce/buy-now', {
        productId: product._id,
        quantity,
        variant: selectedVariant
      });
      
      if (response.data.success) {
        // Navigate to checkout page
        navigate('/ecommerce/checkout');
      }
    } catch (error) {
      console.error('Error processing buy now:', error);
      alert(error.response?.data?.message || 'Error processing buy now');
    } finally {
      setBuyingNow(false);
    }
  };

  const formatPrice = (price) => formatINR(price);

  const getCurrentPrice = () => {
    if (!product) return 0;
    return product.inventory.discountedPrice || product.inventory.sellingPrice;
  };

  const isInStock = () => {
    return product && product.inventory.itemQuantity > 0;
  };

  if (loading) {
    return (
      <div className="product-detail-container">
        <div className="loading">Loading product...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-container">
        <div className="error">Product not found</div>
      </div>
    );
  }

  return (
    <div className="ecommerce-page">
      <EcommerceNav />
      <div className="product-detail-container">
        <div className="product-detail-content">
        {/* Product Images */}
        <div className="product-images">
          <div className="main-image">
            <img
              src={
                product.inventory.images && product.inventory.images.length > 0
                  ? product.inventory.images[selectedImage]
                  : '/placeholder-product.jpg'
              }
              alt={product.inventory.itemName}
              onError={(e) => {
                e.target.src = '/placeholder-product.jpg';
              }}
            />
          </div>
          
          {product.inventory.images && product.inventory.images.length > 1 && (
            <div className="thumbnail-images">
              {product.inventory.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${product.inventory.itemName} ${index + 1}`}
                  className={selectedImage === index ? 'active' : ''}
                  onClick={() => setSelectedImage(index)}
                  onError={(e) => {
                    e.target.src = '/placeholder-product.jpg';
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="product-info">
          <div className="product-header">
            <h1 className="product-title">{product.inventory.itemName}</h1>
            <p className="product-code">SKU: {product.inventory.itemCode}</p>
          </div>

          <div className="product-rating">
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <span
                  key={i}
                  className={`star ${
                    i < Math.floor(product.averageRating) ? 'filled' : ''
                  }`}
                >
                  ★
                </span>
              ))}
            </div>
            <span className="rating-text">
              {product.averageRating} ({product.reviewCount} reviews)
            </span>
          </div>

          <div className="product-price">
            {product.inventory.discountedPrice ? (
              <>
                <span className="current-price">
                  {formatPrice(product.inventory.discountedPrice)}
                </span>
                <span className="original-price">
                  {formatPrice(product.inventory.sellingPrice)}
                </span>
                <span className="discount-badge">
                  {Math.round(
                    ((product.inventory.sellingPrice - product.inventory.discountedPrice) /
                      product.inventory.sellingPrice) *
                      100
                  )}% OFF
                </span>
              </>
            ) : (
              <span className="current-price">
                {formatPrice(product.inventory.sellingPrice)}
              </span>
            )}
          </div>

          <div className="product-description">
            <h3>Description</h3>
            <p>{product.inventory.productDescription}</p>
          </div>

          {/* Product Variants */}
          {product.variants && product.variants.length > 0 && (
            <div className="product-variants">
              <h3>Options</h3>
              <div className="variant-options">
                {product.variants.map((variant, index) => (
                  <button
                    key={index}
                    className={`variant-option ${
                      selectedVariant?.name === variant.name &&
                      selectedVariant?.value === variant.value
                        ? 'selected'
                        : ''
                    }`}
                    onClick={() =>
                      setSelectedVariant({
                        name: variant.name,
                        value: variant.value
                      })
                    }
                  >
                    {variant.name}: {variant.value}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity and Add to Cart */}
          <div className="product-actions">
            <div className="quantity-selector">
              <label>Quantity:</label>
              <div className="quantity-controls">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span>{quantity}</span>
                <button
                  onClick={() =>
                    setQuantity(
                      Math.min(
                        product.inventory.itemQuantity,
                        quantity + 1
                      )
                    )
                  }
                  disabled={quantity >= product.inventory.itemQuantity}
                >
                  +
                </button>
              </div>
            </div>

            <div className="action-buttons">
              <button
                className="buy-now-btn"
                onClick={handleBuyNow}
                disabled={!isInStock() || buyingNow}
              >
                {buyingNow ? 'Processing...' : 'Buy Now'}
              </button>

              <button
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={!isInStock() || addingToCart}
              >
                {addingToCart ? 'Adding...' : 'Add to Cart'}
              </button>

              <button
                className={`wishlist-btn ${inWishlist ? 'in-wishlist' : ''}`}
                onClick={handleAddToWishlist}
                disabled={addingToWishlist}
              >
                {addingToWishlist
                  ? 'Updating...'
                  : inWishlist
                  ? 'In Wishlist'
                  : 'Add to Wishlist'}
              </button>
            </div>
          </div>

          <div className="product-stock">
            {isInStock() ? (
              <span className="in-stock">
                ✓ {product.inventory.itemQuantity} in stock
              </span>
            ) : (
              <span className="out-of-stock">✗ Out of stock</span>
            )}
          </div>

          {/* Product Features */}
          {product.features && product.features.length > 0 && (
            <div className="product-features">
              <h3>Features</h3>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Product Tags */}
          {product.tags && product.tags.length > 0 && (
            <div className="product-tags">
              <h3>Tags</h3>
              <div className="tags">
                {product.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <div className="reviews-section">
          <h2>Customer Reviews</h2>
          <div className="reviews-list">
            {reviews.map((review) => (
              <div key={review._id} className="review-item">
                <div className="review-header">
                  <div className="reviewer-info">
                    <h4>{review.user.name}</h4>
                    <div className="review-rating">
                      {[...Array(5)].map((_, i) => (
                        <span
                          key={i}
                          className={`star ${
                            i < review.rating ? 'filled' : ''
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="review-date">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                </div>
                
                {review.title && <h5 className="review-title">{review.title}</h5>}
                
                {review.comment && (
                  <p className="review-comment">{review.comment}</p>
                )}
                
                {review.isVerified && (
                  <span className="verified-badge">Verified Purchase</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
      <EcommerceFooter />
    </div>
  );
};

export default ProductDetail;
